import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "../Header";
import StatusResultModal from "../StatusResultModal";
import ConfirmModal from "../ConfirmModal";
import Sidebar from "../Sidebar";
import { isValidTargetUrl } from "./UrlInput";
import CustomTargetConfig from "./CustomTargetConfig";
import {
  minifyJSON,
  buildCustomTargetParams,
  CUSTOM_TARGET_PY_SCRIPTS,
  mapTargetConfigToForm,
} from "./customTargetHelpers";
import "../../styles/theme.css";
import "../../styles/ProfilesManager.css";
import "../../styles/Generate.css";
import { useSidebar } from "../../App";
import { getApiUrl, getCsrfToken } from "../../utils/apiHelper";
import { targetLabels, profileTypeLabels } from "../../utils/categoryLabels";
import { targetsToProfileTypes } from "../../utils/targets";

const formatProfileTypesList = (profileTypes) => {
  const labels = (profileTypes || []).map(
    (type) => profileTypeLabels[type] || type
  );
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

// Mirror theme-aware styling helper from ProfilesManager
const getCurrentTheme = () => {
  return document.body.getAttribute("data-theme") || "dark";
};

// Models by provider (for Simulators dropdown)
const SIMULATOR_MODEL_VERSIONS_BY_PROVIDER = {
  "OpenAI-compatible": [
    "gpt-4o-mini",
    "gpt-4.1",
  ],
  "Anthropic Claude-compatible": [
    "claude-3-5-sonnet-latest",
  ],
  "Google Gemini-compatible": [
    "gemini-1.5-pro",
  ],
  "AWS Bedrock-compatible": [
    "amazon.nova-pro-v1:0",
  ],
  "Mistral-compatible": [
    "mistral-large-latest",
  ],
  "Cohere-compatible": [
    "command-r-plus",
  ],
  "Groq-compatible": [
    "llama-3.1-70b-versatile",
  ],
  "Together-compatible": [
    "meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo",
  ],
  "xAI-compatible": [
    "grok-2",
  ],
  "Perplexity-compatible": [
    "sonar-pro",
  ],
};

// Models by provider (for Existing Models dropdown)
const EXISTING_MODEL_VERSIONS_BY_PROVIDER = {
  gemini: [
    "Gemini 3 Pro",
    "Gemini 3 Flash",
    "Gemini 2.5 Pro",
    "Gemini 2.5 Flash",
  ],
  openai: [
    "GPT-5.2",
    "GPT-5.1",
    "GPT-5",
    "GPT-5-mini",
    "o4-mini",
    "o3",
    "o3-mini",
    "o1",
    "GPT-4o",
    "GPT-4 Turbo",
    "GPT-3.5 Turbo",
  ],
  anthopic: [
    "Claude Opus 4.6",
    "Claude Opus 4.5",
    "Claude 3.7 Sonnet",
    "Claude Haiku 4.5",
  ],
  xai: [
    "Grok-4",
    "Grok-3",
    "Grok-3 Mini",
  ],
  meta: [
    "Llama 4 Scout",
    "Llama 3.3 70B",
    "Llama 3.1 405B",
  ],
  huggingface: [
    "Qwen 3",
    "Gemma 2",
    "DeepSeek R1",
    "DeepSeek V3",
    "Mixtral 8x22B",
    "Phi-3",
  ],
  mistral: [
    "Mistral Large 2",
    "Mistral Medium",
    "Mistral Small",
  ],
  cohere: [
    "Command R+",
    "Command R",
    "Command",
  ],
  azure: [
    "Azure OpenAI GPT-5",
    "Azure OpenAI GPT-4.1",
    "Azure OpenAI GPT-35",
    "Azure OpenAI GPT-3",
  ],
  groq: [
    "Groq LLM 5 Series",
    "Groq LLM 4 Series",
  ],
  deepseek: [
    "DeepSeek R1",
    "DeepSeek Coder",
    "DeepSeek Chat",
  ],
  nomic: [
    "Nomic Embed v1.5",
    "Nomic Text",
  ],
};

const DEFAULT_EXISTING_PROVIDER = "gemini";
const DEFAULT_SIMULATOR_PROVIDER = "Google Gemini-compatible";
const EXISTING_PROVIDER_KEYS = [
  "gemini",
  "openai",
  "anthopic",
  "xai",
  "meta",
  "huggingface",
  "mistral",
  "cohere",
  "azure",
  "groq",
  "deepseek",
  "nomic",
];
const SIMULATOR_PROVIDER_KEYS = Object.keys(SIMULATOR_MODEL_VERSIONS_BY_PROVIDER);

function withCurrentOption(options, current) {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
}

export default function AiTargets() {
  const { t, i18n } = useTranslation("targets");
  const { sidebarCollapsed } = useSidebar();
  const locale = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  // Local-only data for now (no backend)
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortAsc, setSortAsc] = useState(false);
  const INITIAL_NEW_TARGET = {
    name: "",
    description: "",
    target_type: "api_target",
    url: "",
    apiConfigMode: CUSTOM_TARGET_PY_SCRIPTS,
    requestParams: "{}",
    responseParams: "{}",
    input_script_prompt_processing: "single",
  };

  const [isNewTargetOpen, setIsNewTargetOpen] = useState(false);
  const [targetPage, setTargetPage] = useState(1);
  const [llmTab, setLlmTab] = useState('existing-models'); // 'existing-models' | 'simulators' | 'custom-targets'
  const [newTarget, setNewTarget] = useState(INITIAL_NEW_TARGET);
  const [llmProvider, setLlmProvider] = useState(DEFAULT_EXISTING_PROVIDER);
  const [llmModelVersion, setLlmModelVersion] = useState(EXISTING_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_EXISTING_PROVIDER][0] || "");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [simProvider, setSimProvider] = useState(DEFAULT_SIMULATOR_PROVIDER);
  const [simModelVersion, setSimModelVersion] = useState(
    SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_SIMULATOR_PROVIDER][0] || ""
  );
  const [simApiKey, setSimApiKey] = useState("");
  const [advancedOptions, setAdvancedOptions] = useState(null);
  const [advancedSeed, setAdvancedSeed] = useState(null);
  const [existingScriptNames, setExistingScriptNames] = useState({ input: "", output: "" });
  const [viewingTargetId, setViewingTargetId] = useState(null);
  const [apiInputScriptFile, setApiInputScriptFile] = useState(null);
  const [apiOutputScriptFile, setApiOutputScriptFile] = useState(null);
  const [mcpInputScriptFile, setMcpInputScriptFile] = useState(null);
  const [mcpOutputScriptFile, setMcpOutputScriptFile] = useState(null);
  const [llmInputScriptFile, setLlmInputScriptFile] = useState(null);
  const [llmOutputScriptFile, setLlmOutputScriptFile] = useState(null);
  const apiScriptsParamsRef = useRef(null);
  const mcpScriptsParamsRef = useRef(null);
  const llmScriptsParamsRef = useRef(null);
  const viewAbortRef = useRef(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null); // id of target being deleted
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Fetch all target configs on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchTargets() {
      setTargetsLoading(true);
      try {
        const response = await fetch(getApiUrl("/api/internal/target-config/getAllTargetConfigs/"), {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            detail: `HTTP error! status: ${response.status}`,
          }));
          throw new Error(errorData.detail || errorData.error || t("alerts.loadFailed"));
        }

        const data = await response.json();

        if (cancelled) return;

        // API returns: [{ id, name, target_type, last_modified }, ...]
        const list = Array.isArray(data) ? data : (data.data ?? data.results ?? []);
        setTargets(
          list.map((item) => ({
            id: item.id,
            name: item.name ?? "",
            target_type: item.target_type ?? "",
            lastModified: item.last_modified ?? "",
          }))
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading Targets:", error);
          setStatusModal({
            open: true,
            type: "error",
            message: error.message || t("alerts.loadFailed"),
          });
          setTargets([]);
        }
      } finally {
        if (!cancelled) setTargetsLoading(false);
      }
    }

    fetchTargets();
    return () => {
      cancelled = true;
    };
  }, []);


  const filteredTargets = useMemo(() => {
    if (!searchQuery.trim()) return targets;
    const q = searchQuery.toLowerCase();
    return targets.filter((e) => {
      return (
        String(e.id).toLowerCase().includes(q) ||
        (e.name || "").toLowerCase().includes(q) ||
        (e.target_type || "").toLowerCase().includes(q)
      );
    });
  }, [searchQuery, targets]);

  const handleOpenNewTarget = () => {
    setNewTarget({ ...INITIAL_NEW_TARGET });
    setApiInputScriptFile(null);
    setApiOutputScriptFile(null);
    setMcpInputScriptFile(null);
    setMcpOutputScriptFile(null);
    setLlmInputScriptFile(null);
    setLlmOutputScriptFile(null);
    setLlmProvider(DEFAULT_EXISTING_PROVIDER);
    setLlmModelVersion(EXISTING_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_EXISTING_PROVIDER][0] || "");
    setLlmApiKey("");
    setSimProvider(DEFAULT_SIMULATOR_PROVIDER);
    setSimModelVersion(SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_SIMULATOR_PROVIDER][0] || "");
    setSimApiKey("");
    setSubmitLoading(false);
    setTargetPage(1);
    setLlmTab('existing-models');
    setAdvancedSeed(null);
    setExistingScriptNames({ input: "", output: "" });
    setViewingTargetId(null);
    setIsNewTargetOpen(true);
  };

  const handleCloseNewTarget = () => {
    // Reset file input refs via component refs
    if (apiScriptsParamsRef.current) apiScriptsParamsRef.current.reset();
    if (mcpScriptsParamsRef.current) mcpScriptsParamsRef.current.reset();
    if (llmScriptsParamsRef.current) llmScriptsParamsRef.current.reset();

    viewAbortRef.current?.abort();
    setSubmitLoading(false);
    setAdvancedSeed(null);
    setExistingScriptNames({ input: "", output: "" });
    setViewingTargetId(null);
    setIsNewTargetOpen(false);
  };

  const handleSaveNewTarget = async () => {
    if (!newTarget.name.trim()) return;

    const target_type = newTarget.target_type || "api_target";

    // Build targetParams: selection, url, model details, and optional JSON parameters
    let targetParams;
    let selection;

    if (newTarget.target_type === "llm_model_target") {
      if (llmTab === "existing-models") {
        if (!llmApiKey.trim()) {
          setStatusModal({
            open: true,
            type: "error",
            message: t("alerts.apiKeyRequiredExistingModel"),
          });
          return;
        }

        selection = "model";
        targetParams = {
          url: "",
          selection,
          model_details: {
            provider: llmProvider || "",
            version: llmModelVersion || "",
            api_key: llmApiKey || "",
          },
        };
      } else if (llmTab === "simulators") {
        if (!simApiKey.trim()) {
          setStatusModal({
            open: true,
            type: "error",
            message: t("alerts.apiKeyRequiredSimulator"),
          });
          return;
        }

        selection = "simulator";
        targetParams = {
          url: "",
          selection,
          model_details: {
            provider: simProvider || "",
            version: simModelVersion || "",
            api_key: simApiKey || "",
          },
        };
      } else {
        ({ selection, targetParams } = buildCustomTargetParams({
          url: newTarget.url || "",
          apiConfigMode: newTarget.apiConfigMode,
          promptsProcessing: newTarget.input_script_prompt_processing,
          advancedOptions,
        }));
      }
    } else {
      ({ selection, targetParams } = buildCustomTargetParams({
        url: newTarget.url || "",
        apiConfigMode: newTarget.apiConfigMode,
        promptsProcessing: newTarget.input_script_prompt_processing,
        advancedOptions,
      }));
    }

    // Validate URL/IP for all target types that use a URL
    if (selection !== "model" && selection !== "simulator") {
      const urlToValidate = targetParams.url;
      if (!urlToValidate || !isValidTargetUrl(urlToValidate)) {
        setStatusModal({
          open: true,
          type: "error",
          message: t("alerts.invalidUrl"),
        });
        return;
      }
    }

    setSubmitLoading(true);

    // Prepare FormData for API submission
    const formData = new FormData();

    formData.append("target_name", newTarget.name.trim());
    formData.append("target_desc", newTarget.description || "");
    formData.append("target_type", target_type);
    // json_parameters is a separate form field. target_params keeps url, selection, and mode-specific keys.
    if (selection === "json_parameters") {
      formData.append("json_parameters", JSON.stringify({
        request: minifyJSON(newTarget.requestParams),
        response: minifyJSON(newTarget.responseParams),
      }));
    }
    formData.append("target_params", JSON.stringify(targetParams));

    // When selection is "scripts", upload files
    if (selection === "scripts") {
      if (newTarget.target_type === "api_target") {
        if (apiInputScriptFile) formData.append("inputScript", apiInputScriptFile);
        if (apiOutputScriptFile) formData.append("outputScript", apiOutputScriptFile);
      } else if (newTarget.target_type === "mcp_target") {
        if (mcpInputScriptFile) formData.append("inputScript", mcpInputScriptFile);
        if (mcpOutputScriptFile) formData.append("outputScript", mcpOutputScriptFile);
      } else if (newTarget.target_type === "llm_model_target") {
        if (llmInputScriptFile) formData.append("inputScript", llmInputScriptFile);
        if (llmOutputScriptFile) formData.append("outputScript", llmOutputScriptFile);
      }
    }

    const isUpdate = viewingTargetId != null;
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(
          isUpdate
            ? `/api/internal/target-config/${viewingTargetId}/`
            : "/api/internal/target-config/"
        ),
        {
          method: "POST",
          headers: {
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP error! status: ${response.status}`
        }));
        throw new Error(
          errorData.detail || errorData.error || t(isUpdate ? "alerts.updateFailed" : "alerts.createFailed")
        );
      }

      const data = await response.json();

      if (data.success) {
        const saved = {
          id: data.data.id ?? viewingTargetId,
          name: data.data.target_name ?? newTarget.name.trim(),
          target_type: data.data.target_type ?? target_type,
          lastModified: data.data.updated_at ?? data.data.created_at,
        };
        setTargets((prev) =>
          isUpdate
            ? prev.map((ep) => (ep.id === viewingTargetId ? { ...ep, ...saved } : ep))
            : [...prev, saved]
        );
        setStatusModal({
          open: true,
          type: "success",
          message: data.detail || t(isUpdate ? "alerts.updateSuccess" : "alerts.createSuccess"),
        });
        setViewingTargetId(null);
        setIsNewTargetOpen(false);
      } else {
        throw new Error(data.detail || t(isUpdate ? "alerts.updateFailed" : "alerts.createFailed"));
      }
    } catch (error) {
      console.error(isUpdate ? "Error updating Target:" : "Error creating Target:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || t(isUpdate ? "alerts.updateError" : "alerts.createError"),
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteTarget = async (ep) => {
    setDeletingId(ep.id);

    try {
      const csrfToken = await getCsrfToken();

      const response = await fetch(getApiUrl(`/api/internal/target-config/${ep.id}/delete/`), {
        method: "DELETE",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP error! status: ${response.status}`,
        }));
        throw new Error(errorData.detail || errorData.error || t("alerts.deleteFailed"));
      }

      const data = await response.json().catch(() => ({}));

      setTargets((prev) => prev.filter((e) => e.id !== ep.id));
      setStatusModal({
        open: true,
        type: "success",
        message: data.detail || t("alerts.deleteSuccess"),
      });
    } catch (error) {
      console.error("Error deleting Target:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || t("alerts.deleteError"),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("notAvailable");
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString(locale) +
        " " +
        date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return t("invalidDate");
    }
  };

  const applyTargetConfigToForm = (data) => {
    const mapped = mapTargetConfigToForm(data);
    setNewTarget(mapped.target);
    setApiInputScriptFile(null);
    setApiOutputScriptFile(null);
    setMcpInputScriptFile(null);
    setMcpOutputScriptFile(null);
    setLlmInputScriptFile(null);
    setLlmOutputScriptFile(null);
    setLlmTab(mapped.llmTab);
    if (mapped.llmTab === "existing-models") {
      setLlmProvider(mapped.provider || DEFAULT_EXISTING_PROVIDER);
      setLlmModelVersion(
        mapped.modelVersion ||
          EXISTING_MODEL_VERSIONS_BY_PROVIDER[mapped.provider || DEFAULT_EXISTING_PROVIDER]?.[0] ||
          ""
      );
      setLlmApiKey(mapped.apiKey);
      setSimProvider(DEFAULT_SIMULATOR_PROVIDER);
      setSimModelVersion(SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_SIMULATOR_PROVIDER][0] || "");
      setSimApiKey("");
    } else if (mapped.llmTab === "simulators") {
      setSimProvider(mapped.provider || DEFAULT_SIMULATOR_PROVIDER);
      setSimModelVersion(
        mapped.modelVersion ||
          SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[mapped.provider || DEFAULT_SIMULATOR_PROVIDER]?.[0] ||
          ""
      );
      setSimApiKey(mapped.apiKey);
      setLlmProvider(DEFAULT_EXISTING_PROVIDER);
      setLlmModelVersion(EXISTING_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_EXISTING_PROVIDER][0] || "");
      setLlmApiKey("");
    } else {
      setLlmProvider(DEFAULT_EXISTING_PROVIDER);
      setLlmModelVersion(EXISTING_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_EXISTING_PROVIDER][0] || "");
      setLlmApiKey("");
      setSimProvider(DEFAULT_SIMULATOR_PROVIDER);
      setSimModelVersion(SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[DEFAULT_SIMULATOR_PROVIDER][0] || "");
      setSimApiKey("");
    }
    setAdvancedOptions(mapped.advancedSeed);
    setAdvancedSeed(mapped.advancedSeed);
    setExistingScriptNames(mapped.existingScripts);
    setViewingTargetId(data.id ?? null);
    setSubmitLoading(false);
    setTargetPage(1);
    setIsNewTargetOpen(true);
  };

  const handleViewTarget = async (ep) => {
    viewAbortRef.current?.abort();
    viewAbortRef.current = new AbortController();
    const signal = viewAbortRef.current.signal;
    setViewLoadingId(ep.id);
    try {
      const response = await fetch(getApiUrl(`/api/internal/target-config/getTargetConfigById/${ep.id}/`), {
        method: "GET",
        credentials: "include",
        signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || data.error || t("alerts.loadByIdFailed", { status: response.status }));
      }
      if (signal.aborted) return;
      applyTargetConfigToForm(data);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error loading Target Config:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || t("alerts.loadConfigFailed"),
      });
    } finally {
      if (viewAbortRef.current?.signal === signal) setViewLoadingId(null);
    }
  };

  const handleDownloadTargetFile = async (targetConfigId, fileName) => {
    const name = String(fileName || "").trim();
    if (!targetConfigId || !name || downloadingFile) return;
    setDownloadingFile(name);
    try {
      const response = await fetch(
        getApiUrl(
          `/api/internal/target-config/download/${targetConfigId}/?file=${encodeURIComponent(name)}`
        ),
        { method: "GET", credentials: "include" }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || t("alerts.downloadFileFailed"));
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading target file:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || t("alerts.downloadFileFailed"),
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <div className="theme-whbl">
      <Header />
      <ConfirmModal
        open={pendingDelete != null}
        title={t("deleteTarget")}
        message={
          pendingDelete
            ? t("alerts.deleteConfirm", { name: pendingDelete.name })
            : ""
        }
        confirmLabel={t("deleteTarget")}
        cancelLabel={t("cancel")}
        zIndex={2100}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const ep = pendingDelete;
          setPendingDelete(null);
          if (ep) handleDeleteTarget(ep);
        }}
      />
      <StatusResultModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        zIndex={2100}
        onClose={() =>
          setStatusModal({ open: false, type: "success", message: "" })
        }
      />

      <div className="main-container">
        {/* Sidebar column */}
        <div className={`${sidebarCollapsed ? "col-1" : "col-2"} p-0`}>
          <Sidebar />
        </div>

        {/* Main content column */}
        <main
          className={`${sidebarCollapsed ? "col-11" : "col-10"}`}
          style={{ width: "100%", overflowX: "auto" }}
        >
          <div className="dashboard">
            <div className="pm-header">
              <div className="pm-title-section">
                <h1 className="pm-title">{t("title")}</h1>
              </div>
              <button
                className="pm-create-btn"
                type="button"
                onClick={handleOpenNewTarget}
              >
                {t("newTarget")}
              </button>
            </div>

            <div className="pm-search-container">
              <input
                type="text"
                className="pm-search-input"
                placeholder={t("searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="pm-profile-table">
              <div className="pm-profile-table-header">
                {[
                  ["id", t("table.id"), ""],
                  ["name", t("table.name"), "pm-profile-table-cell-name"],
                  ["target_type", t("table.type"), "pm-profile-table-cell-type"],
                  ["lastModified", t("table.lastModified"), "pm-profile-table-cell-last-modified"],
                ].map(([key, label, cls]) => (
                  <div
                    key={key}
                    className={`pm-profile-table-cell ${cls}`.trim()}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sortBy === key) setSortAsc(!sortAsc);
                      else { setSortBy(key); setSortAsc(true); }
                    }}
                  >
                    {label}{sortBy === key ? (sortAsc ? " ▲" : " ▼") : ""}
                  </div>
                ))}
                <div className="pm-profile-table-cell pm-profile-table-cell-actions">
                  {t("table.actions")}
                </div>
              </div>
              <div className="pm-profile-table-body">
                {targetsLoading ? (
                  <div className="pm-profile-table-row">
                    <div className="pm-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                      {t("loading")}
                    </div>
                  </div>
                ) : (
                  <>
                    {[...filteredTargets]
                      .sort((a, b) => {
                        const dir = sortAsc ? 1 : -1;
                        if (sortBy === "id") return ((Number(a.id) || 0) - (Number(b.id) || 0)) * dir;
                        if (sortBy === "lastModified") {
                          return ((a.lastModified ? new Date(a.lastModified).getTime() : 0) - (b.lastModified ? new Date(b.lastModified).getTime() : 0)) * dir;
                        }
                        const aVal = sortBy === "target_type"
                          ? (targetLabels[a.target_type] ?? a.target_type ?? "")
                          : (a[sortBy] || "");
                        const bVal = sortBy === "target_type"
                          ? (targetLabels[b.target_type] ?? b.target_type ?? "")
                          : (b[sortBy] || "");
                        return String(aVal).localeCompare(String(bVal)) * dir;
                      })
                      .map((ep) => (
                        <div key={ep.id} className="pm-profile-table-row">
                          <div className="pm-profile-table-cell">
                            <div>{ep.id}</div>
                          </div>
                          <div className="pm-profile-table-cell pm-profile-table-cell-name">
                            <div className="pm-profile-name" title={ep.name}>{ep.name}</div>
                          </div>
                          <div className="pm-profile-table-cell pm-profile-table-cell-type">
                            <div className="pm-profile-type">{targetLabels[ep.target_type] ?? ep.target_type}</div>
                          </div>
                          <div className="pm-profile-table-cell pm-profile-table-cell-last-modified">
                            <div className="pm-profile-last-modified">
                              {formatDate(ep.lastModified)}
                            </div>
                          </div>
                          <div className="pm-profile-table-cell pm-profile-table-cell-actions">
                            <div className="pm-profile-action-buttons">
                              <button
                                type="button"
                                className="pm-action-btn pm-edit-btn"
                                title={t("editTarget")}
                                onClick={() => handleViewTarget(ep)}
                                disabled={viewLoadingId === ep.id}
                              >
                                {viewLoadingId === ep.id ? (
                                  <i className="fas fa-spinner fa-spin" />
                                ) : (
                                  <i className="fas fa-edit" />
                                )}
                              </button>
                              <button
                                type="button"
                                className="pm-action-btn pm-delete-btn"
                                title={t("deleteTarget")}
                                onClick={() => setPendingDelete(ep)}
                                disabled={deletingId === ep.id}
                              >
                                {deletingId === ep.id ? (
                                  <i className="fas fa-spinner fa-spin" />
                                ) : (
                                  <i className="fas fa-trash" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                    {filteredTargets.length === 0 && (
                      <div className="pm-profile-table-row">
                        <div className="pm-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                          {t("empty")}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {isNewTargetOpen && (
              <div
                className="pm-modal-overlay"
                onClick={handleCloseNewTarget}
              >
                <div
                  className="pm-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", paddingBottom: "16px", borderBottom: `1px solid ${getCurrentTheme() === "light" ? "#e5e7eb" : "#374151"}` }}>
                    <h2 className="pm-modal-title" style={{ margin: 0 }}>
                      {viewingTargetId != null ? t("editTarget") : t("createTarget")}
                    </h2>
                    <button
                      type="button"
                      onClick={handleCloseNewTarget}
                      title={t("close")}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "20px",
                        cursor: "pointer",
                        color: getCurrentTheme() === "light" ? "#6b7280" : "#9ca3af",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget;
                        el.style.color = getCurrentTheme() === "light" ? "#374151" : "#ffffff";
                        el.style.backgroundColor = getCurrentTheme() === "light" ? "#f3f4f6" : "#374151";
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget;
                        el.style.color = getCurrentTheme() === "light" ? "#6b7280" : "#9ca3af";
                        el.style.backgroundColor = "transparent";
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <label className="pm-input-label">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span>{t("form.targetName")}</span>
                      <div
                        style={{
                          fontSize: "12px",
                          color:
                            (newTarget.name?.length || 0) > 100
                              ? "#ef4444"
                              : "#9ca3af",
                        }}
                      >
                        {(newTarget.name?.length || 0)} / 100
                      </div>
                    </div>
                    <input
                      className="pm-input"
                      value={newTarget.name}
                      onChange={(e) =>
                        setNewTarget((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder={t("form.targetNamePlaceholder")}
                      maxLength={100}
                    />
                  </label>

                  <label className="pm-input-label">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span>{t("form.targetDescription")}</span>
                      <div
                        style={{
                          fontSize: "12px",
                          color:
                            (newTarget.description?.length || 0) > 500
                              ? "#ef4444"
                              : "#9ca3af",
                        }}
                      >
                        {(newTarget.description?.length || 0)} / 500
                      </div>
                    </div>
                    <textarea
                      className="pm-input"
                      rows="3"
                      value={newTarget.description}
                      onChange={(e) =>
                        setNewTarget((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder={t("form.targetDescriptionPlaceholder")}
                      maxLength={500}
                    />
                  </label>

                  <div className="pm-tabs">
                    <div className="pm-tab-content" style={{ marginTop: 0 }}>
                      <div style={{ padding: "20px" }}>
                        {targetPage === 1 && (
                          <>
                            <h3
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                marginBottom: "16px",
                                color:
                                  getCurrentTheme() === "light"
                                    ? "#000000"
                                    : "#ffffff",
                              }}
                            >
                              {t("form.selectAiTarget")}
                            </h3>

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              {[
                                "api_target",
                                "llm_model_target",
                                "mcp_target",
                              ].map((targetType) => {
                                const isSelected =
                                  newTarget.target_type === targetType;
                                const profileTypes =
                                  targetsToProfileTypes[targetType] || [];
                                return (
                                  <label
                                    key={targetType}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      padding: "16px",
                                      borderRadius: "8px",
                                      border: isSelected
                                        ? "2px solid #2563eb"
                                        : "1px solid #374151",
                                      background: isSelected
                                        ? "#1e3a8a"
                                        : "#111827",
                                      color:
                                        getCurrentTheme() === "light"
                                          ? "#000000"
                                          : "#ffffff",
                                      cursor: "pointer",
                                      opacity: 1,
                                      transition: "all 0.2s ease",
                                      boxShadow: isSelected
                                        ? "0 0 0 3px rgba(37,99,235,0.25)"
                                        : "none",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name="target-type"
                                      value={targetType}
                                      checked={isSelected}
                                      onChange={(e) =>
                                        setNewTarget((prev) => ({
                                          ...prev,
                                          target_type: e.target.value,
                                        }))
                                      }
                                      style={{
                                        marginRight: "12px",
                                        marginTop: "2px",
                                        width: "18px",
                                        height: "18px",
                                        cursor: "pointer",
                                        accentColor: "#2563eb",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <div>
                                      <div
                                        style={{
                                          fontWeight: 600,
                                          fontSize: "15px",
                                        }}
                                      >
                                        {targetLabels[targetType]}
                                      </div>
                                      <div
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "13px",
                                          fontWeight: 400,
                                          lineHeight: 1.4,
                                          opacity: 0.8,
                                        }}
                                      >
                                        {t("form.targetTypeProfileTypes", {
                                          types: formatProfileTypesList(
                                            profileTypes
                                          ),
                                        })}
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                          </>
                        )}

                        {targetPage === 2 && (
                          <>
                            <h3
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                marginBottom: "8px",
                                color:
                                  getCurrentTheme() === "light"
                                    ? "#000000"
                                    : "#ffffff",
                              }}
                            >
                              {t("form.details", { type: targetLabels[newTarget.target_type] || t("form.targetFallback") })}
                            </h3>
                            <div
                              style={{
                                marginBottom: "16px",
                                fontSize: "13px",
                                fontWeight: 400,
                                lineHeight: 1.4,
                                opacity: 0.8,
                                color:
                                  getCurrentTheme() === "light"
                                    ? "#000000"
                                    : "#ffffff",
                              }}
                            >
                              {t("form.targetTypeProfileTypes", {
                                types: formatProfileTypesList(
                                  targetsToProfileTypes[newTarget.target_type] || []
                                ),
                              })}
                            </div>

                            {/* Details content per target type */}
                            {newTarget.target_type === "api_target" && (
                              <div>
                                <CustomTargetConfig
                                  ref={apiScriptsParamsRef}
                                  url={newTarget.url}
                                  onUrlChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      url: e.target.value,
                                    }))
                                  }
                                  mode={newTarget.apiConfigMode}
                                  onModeChange={(mode) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      apiConfigMode: mode,
                                    }))
                                  }
                                  inputScriptFile={apiInputScriptFile}
                                  outputScriptFile={apiOutputScriptFile}
                                  onInputScriptChange={setApiInputScriptFile}
                                  onOutputScriptChange={setApiOutputScriptFile}
                                  inputScriptDescription={t("form.inputScriptDesc")}
                                  outputScriptDescription={t("form.outputScriptDesc")}
                                  promptsProcessing={newTarget.input_script_prompt_processing}
                                  onPromptsProcessingChange={(value) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      input_script_prompt_processing: value,
                                    }))
                                  }
                                  requestParams={newTarget.requestParams}
                                  responseParams={newTarget.responseParams}
                                  onRequestParamsChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      requestParams: e.target.value,
                                    }))
                                  }
                                  onResponseParamsChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      responseParams: e.target.value,
                                    }))
                                  }
                                  onAdvancedOptionsChange={setAdvancedOptions}
                                  advancedSeed={advancedSeed}
                                  existingInputScriptName={existingScriptNames.input}
                                  existingOutputScriptName={existingScriptNames.output}
                                  onDownloadExistingScript={
                                    viewingTargetId
                                      ? (name) => handleDownloadTargetFile(viewingTargetId, name)
                                      : undefined
                                  }
                                />
                              </div>
                            )}

                            {newTarget.target_type === "mcp_target" && (
                              <div>
                                <CustomTargetConfig
                                  ref={mcpScriptsParamsRef}
                                  url={newTarget.url}
                                  onUrlChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      url: e.target.value,
                                    }))
                                  }
                                  mode={newTarget.apiConfigMode}
                                  onModeChange={(mode) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      apiConfigMode: mode,
                                    }))
                                  }
                                  inputScriptFile={mcpInputScriptFile}
                                  outputScriptFile={mcpOutputScriptFile}
                                  onInputScriptChange={setMcpInputScriptFile}
                                  onOutputScriptChange={setMcpOutputScriptFile}
                                  inputScriptDescription={t("form.mcpInputScriptDesc")}
                                  outputScriptDescription={t("form.mcpOutputScriptDesc")}
                                  promptsProcessing={newTarget.input_script_prompt_processing}
                                  onPromptsProcessingChange={(value) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      input_script_prompt_processing: value,
                                    }))
                                  }
                                  requestParams={newTarget.requestParams}
                                  responseParams={newTarget.responseParams}
                                  onRequestParamsChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      requestParams: e.target.value,
                                    }))
                                  }
                                  onResponseParamsChange={(e) =>
                                    setNewTarget((prev) => ({
                                      ...prev,
                                      responseParams: e.target.value,
                                    }))
                                  }
                                  onAdvancedOptionsChange={setAdvancedOptions}
                                  advancedSeed={advancedSeed}
                                  existingInputScriptName={existingScriptNames.input}
                                  existingOutputScriptName={existingScriptNames.output}
                                  onDownloadExistingScript={
                                    viewingTargetId
                                      ? (name) => handleDownloadTargetFile(viewingTargetId, name)
                                      : undefined
                                  }
                                />
                              </div>
                            )}

                            {newTarget.target_type === "llm_model_target" && (
                              <div>
                                {/* Tab Selection */}
                                <div
                                  className="generate-tab-container"
                                  style={{
                                    marginTop: "0",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setLlmTab('existing-models')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setLlmTab('existing-models');
                                      }
                                    }}
                                    className={`generate-tab-button ${llmTab === 'existing-models' ? 'active' : ''}`}
                                    style={{
                                      flex: "1 1 0",
                                      minWidth: "140px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div className="generate-tab-content" style={{ width: "100%", textAlign: "center" }}>
                                      <div className="generate-tab-title">{t("form.existingModels")}</div>
                                    </div>
                                  </div>

                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setLlmTab('simulators')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setLlmTab('simulators');
                                      }
                                    }}
                                    className={`generate-tab-button ${llmTab === 'simulators' ? 'active' : ''}`}
                                    style={{
                                      flex: "1 1 0",
                                      minWidth: "140px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div className="generate-tab-content" style={{ width: "100%", textAlign: "center" }}>
                                      <div className="generate-tab-title">{t("form.simulatorTargets")}</div>
                                    </div>
                                  </div>

                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setLlmTab('custom-targets')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setLlmTab('custom-targets');
                                      }
                                    }}
                                    className={`generate-tab-button ${llmTab === 'custom-targets' ? 'active' : ''}`}
                                    style={{
                                      flex: "1 1 0",
                                      minWidth: "140px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div className="generate-tab-content" style={{ width: "100%", textAlign: "center" }}>
                                      <div className="generate-tab-title">{t("form.customTarget")}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Tab Content */}
                                {llmTab === 'existing-models' && (
                                  <div>
                                    {/* Provider Dropdown */}
                                    <label className="pm-input-label" style={{ marginBottom: "20px" }}>
                                      <span>{t("form.provider")}</span>
                                      <select
                                        className="pm-input"
                                        value={llmProvider}
                                        onChange={(e) => {
                                          const next = e.target.value;
                                          setLlmProvider(next);
                                          const options = EXISTING_MODEL_VERSIONS_BY_PROVIDER[next];
                                          setLlmModelVersion(options?.[0] ?? "");
                                        }}
                                        style={{
                                          padding: "8px 36px 8px 12px",
                                          borderRadius: "6px",
                                          border: "1px solid #374151",
                                          backgroundColor: getCurrentTheme() === "light" ? "#ffffff" : "#1f2937",
                                          color: getCurrentTheme() === "light" ? "#111827" : "#ffffff",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                                          backgroundRepeat: "no-repeat",
                                          backgroundPosition: "right 12px center",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        {withCurrentOption(EXISTING_PROVIDER_KEYS, llmProvider).map((providerKey) => (
                                          <option key={providerKey} value={providerKey}>
                                            {t(`providers.${providerKey}`, { defaultValue: providerKey })}
                                          </option>
                                        ))}
                                      </select>
                                    </label>

                                    {/* Model Dropdown */}
                                    <label className="pm-input-label" style={{ marginBottom: "20px" }}>
                                      <span style={{ display: "block", marginBottom: "8px" }}>{t("form.model")}</span>
                                      <select
                                        className="pm-input"
                                        value={llmModelVersion}
                                        onChange={(e) => setLlmModelVersion(e.target.value)}
                                        style={{
                                          padding: "8px 36px 8px 12px",
                                          borderRadius: "6px",
                                          border: "1px solid #374151",
                                          backgroundColor: getCurrentTheme() === "light" ? "#ffffff" : "#1f2937",
                                          color: getCurrentTheme() === "light" ? "#111827" : "#ffffff",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          width: "100%",
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                                          backgroundRepeat: "no-repeat",
                                          backgroundPosition: "right 12px center",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        {withCurrentOption(
                                          EXISTING_MODEL_VERSIONS_BY_PROVIDER[llmProvider] || [],
                                          llmModelVersion
                                        ).map((opt) => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    </label>

                                    {/* API Key Text Box */}
                                    <label className="pm-input-label">
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        <span>{t("form.apiKey")}</span>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color:
                                              (llmApiKey?.length || 0) > 1000
                                                ? "#ef4444"
                                                : "#9ca3af",
                                          }}
                                        >
                                          {(llmApiKey?.length || 0)} / 1000
                                        </div>
                                      </div>
                                      <input
                                        className="pm-input"
                                        type={viewingTargetId != null ? "text" : "password"}
                                        value={llmApiKey}
                                        onChange={(e) => setLlmApiKey(e.target.value)}
                                        placeholder={t("form.apiKeyPlaceholder")}
                                        maxLength={1000}
                                      />
                                    </label>
                                  </div>
                                )}

                                {llmTab === 'simulators' && (
                                  <div>
                                    <p
                                      style={{
                                        margin: "0 0 16px",
                                        color: getCurrentTheme() === "light" ? "#6b7280" : "#9ca3af",
                                        fontSize: "12px",
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {t("form.simulatorHint")}
                                    </p>
                                    {/* Provider Dropdown */}
                                    <label className="pm-input-label" style={{ marginBottom: "20px" }}>
                                      <span>{t("form.provider")}</span>
                                      <select
                                        className="pm-input"
                                        value={simProvider}
                                        onChange={(e) => {
                                          const next = e.target.value;
                                          setSimProvider(next);
                                          const options = SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[next];
                                          setSimModelVersion(options?.[0] ?? "");
                                        }}
                                        style={{
                                          padding: "8px 36px 8px 12px",
                                          borderRadius: "6px",
                                          border: "1px solid #374151",
                                          backgroundColor: getCurrentTheme() === "light" ? "#ffffff" : "#1f2937",
                                          color: getCurrentTheme() === "light" ? "#111827" : "#ffffff",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                                          backgroundRepeat: "no-repeat",
                                          backgroundPosition: "right 12px center",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        {withCurrentOption(SIMULATOR_PROVIDER_KEYS, simProvider).map((providerKey) => (
                                          <option key={providerKey} value={providerKey}>{providerKey}</option>
                                        ))}
                                      </select>
                                    </label>

                                    {/* Model Dropdown */}
                                    <label className="pm-input-label" style={{ marginBottom: "20px" }}>
                                      <span style={{ display: "block", marginBottom: "8px" }}>{t("form.model")}</span>
                                      <select
                                        className="pm-input"
                                        value={simModelVersion}
                                        onChange={(e) => setSimModelVersion(e.target.value)}
                                        style={{
                                          padding: "8px 36px 8px 12px",
                                          borderRadius: "6px",
                                          border: "1px solid #374151",
                                          backgroundColor: getCurrentTheme() === "light" ? "#ffffff" : "#1f2937",
                                          color: getCurrentTheme() === "light" ? "#111827" : "#ffffff",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          width: "100%",
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                                          backgroundRepeat: "no-repeat",
                                          backgroundPosition: "right 12px center",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        {withCurrentOption(
                                          SIMULATOR_MODEL_VERSIONS_BY_PROVIDER[simProvider] || [],
                                          simModelVersion
                                        ).map((opt) => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    </label>

                                    {/* API Key Text Box */}
                                    <label className="pm-input-label">
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        <span>{t("form.apiKey")}</span>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color:
                                              (simApiKey?.length || 0) > 1000
                                                ? "#ef4444"
                                                : "#9ca3af",
                                          }}
                                        >
                                          {(simApiKey?.length || 0)} / 1000
                                        </div>
                                      </div>
                                      <input
                                        className="pm-input"
                                        type={viewingTargetId != null ? "text" : "password"}
                                        value={simApiKey}
                                        onChange={(e) => setSimApiKey(e.target.value)}
                                        placeholder={t("form.apiKeyPlaceholder")}
                                        maxLength={1000}
                                      />
                                    </label>
                                  </div>
                                )}

                                {llmTab === 'custom-targets' && (
                                  <CustomTargetConfig
                                    ref={llmScriptsParamsRef}
                                    url={newTarget.url}
                                    onUrlChange={(e) =>
                                      setNewTarget((prev) => ({
                                        ...prev,
                                        url: e.target.value,
                                      }))
                                    }
                                    mode={newTarget.apiConfigMode}
                                    onModeChange={(mode) =>
                                      setNewTarget((prev) => ({
                                        ...prev,
                                        apiConfigMode: mode,
                                      }))
                                    }
                                    inputScriptFile={llmInputScriptFile}
                                    outputScriptFile={llmOutputScriptFile}
                                    onInputScriptChange={setLlmInputScriptFile}
                                    onOutputScriptChange={setLlmOutputScriptFile}
                                    inputScriptDescription={t("form.customModelInputScriptDesc")}
                                    outputScriptDescription={t("form.customModelOutputScriptDesc")}
                                    promptsProcessing={newTarget.input_script_prompt_processing}
                                    onPromptsProcessingChange={(value) =>
                                      setNewTarget((prev) => ({
                                        ...prev,
                                        input_script_prompt_processing: value,
                                      }))
                                    }
                                    requestParams={newTarget.requestParams}
                                    responseParams={newTarget.responseParams}
                                    onRequestParamsChange={(e) =>
                                      setNewTarget((prev) => ({
                                        ...prev,
                                        requestParams: e.target.value,
                                      }))
                                    }
                                    onResponseParamsChange={(e) =>
                                      setNewTarget((prev) => ({
                                        ...prev,
                                        responseParams: e.target.value,
                                      }))
                                    }
                                    onAdvancedOptionsChange={setAdvancedOptions}
                                    advancedSeed={advancedSeed}
                                    existingInputScriptName={existingScriptNames.input}
                                    existingOutputScriptName={existingScriptNames.output}
                                    onDownloadExistingScript={
                                      viewingTargetId
                                        ? (name) => handleDownloadTargetFile(viewingTargetId, name)
                                        : undefined
                                    }
                                  />
                                )}
                              </div>
                            )}

                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pm-modal-actions">
                    {targetPage === 1 ? (
                      <>
                        <button
                          type="button"
                          className="pm-close-btn"
                          onClick={handleCloseNewTarget}
                        >
                          {t("cancel")}
                        </button>
                        <button
                          type="button"
                          className="pm-save-btn pm-nav-arrow"
                          onClick={() => setTargetPage(2)}
                          disabled={!newTarget.target_type}
                        >
                          {t("next")}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="pm-close-btn pm-nav-arrow"
                          onClick={() => setTargetPage(1)}
                        >
                          {t("back")}
                        </button>
                        <button
                          type="button"
                          className="pm-save-btn"
                          onClick={handleSaveNewTarget}
                          disabled={!newTarget.name.trim() || submitLoading}
                        >
                          {submitLoading ? (
                            <>
                              <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                              {t("saving")}
                            </>
                          ) : (
                            t("save")
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

